import { Fragment, useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Collapse,
  Drawer,
  Group,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconChevronDown,
  IconChevronRight,
  IconInfoCircle,
} from "@tabler/icons-react";
import { fetchPromptSamples, fetchSampleAnswer } from "./mentionsApi";
import { hostOf, isOwnHost } from "../_Shared/domains";
import AnswerMarkdown from "../_Shared/AnswerMarkdown";
import QueryTypeBadge from "../_Shared/QueryTypeBadge";
import { presenceClassMeta } from "../_Shared/presenceClasses";
import { queryTypeMeta } from "../_Shared/queryTypes";

/**
 * The evidence behind one prompt's count.
 *
 * A detection rate is an assertion about a brand made by a string match, and a number nobody can
 * check is a number nobody should act on. This is where "why does it say we were mentioned" and
 * "why does it say we were not" get answered: which spelling matched, on which day, on which
 * surface, and the answer text itself with the match highlighted in it.
 *
 * Stored answers are kept 90 days; the checks are kept forever, because no AI surface offers
 * historical data and a lost sample cannot be re-taken. Rows past retention therefore show their
 * result and say plainly that the text is gone, rather than offering a link that fails.
 */

/** Small grey chips for a list that is context, not a finding. */
function ChipRow({ label, values }) {
  if (!values?.length) return null;

  return (
    <Group gap={4} wrap="wrap">
      <Text fz="xs" c="dimmed">
        {label}
      </Text>
      {values.map((v) => (
        <Badge key={v} size="xs" variant="light" color="gray">
          {v}
        </Badge>
      ))}
    </Group>
  );
}

function SampleRow({ brandId, sample, ownDomain }) {
  const [open, setOpen] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const toggle = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (answer || loading) return;

    setLoading(true);
    setError(null);
    try {
      setAnswer(await fetchSampleAnswer(brandId, sample.id));
    } catch (e) {
      setError(e?.message ?? "The answer could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  const failed = Boolean(sample.failureReason);

  // Read from the sample's own domains, so it answers for checks taken long before the citation
  // and the mention were told apart.
  const citedNotNamed =
    !failed &&
    !sample.mentioned &&
    (sample.sourceDomains ?? []).some((d) => isOwnHost(ownDomain, hostOf(d) ?? d));

  return (
    <Card withBorder padding="sm">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs">
            <Text fz="sm" fw={600}>
              {new Date(sample.sampledAt).toLocaleDateString()}
            </Text>
            <Text fz="xs" c="dimmed">
              {sample.surfaceName}
            </Text>

            {failed ? (
              <Badge size="sm" color="yellow" variant="light">
                Check failed
              </Badge>
            ) : sample.mentioned ? (
              <Badge size="sm" color="green" variant="light">
                Mentioned
              </Badge>
            ) : (
              <Badge size="sm" color="gray" variant="light">
                Not mentioned
              </Badge>
            )}

            {/* The answer read this brand's own page and named somebody else. Not a mention —
                the reader never saw the name — and not nothing either. */}
            {citedNotNamed && (
              <Badge size="sm" color="blue" variant="light">
                Cited as a source
              </Badge>
            )}
          </Group>

          {failed && (
            <Text fz="xs" c="dimmed">
              {sample.failureReason} — counted as neither a mention nor a miss.
            </Text>
          )}

          <ChipRow label="Matched:" values={sample.matchedTerms} />
          <ChipRow label="Competitors:" values={sample.competitorsMentioned} />
          <ChipRow label="Sources:" values={sample.sourceDomains?.slice(0, 6)} />
        </Stack>

        {sample.hasStoredAnswer ? (
          <Button
            size="compact-sm"
            variant="subtle"
            onClick={toggle}
            leftSection={
              open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />
            }
          >
            Answer
          </Button>
        ) : (
          <Text fz="xs" c="dimmed" ta="right" w={130}>
            {failed ? "No answer to show" : "Answer expired (kept 90 days)"}
          </Text>
        )}
      </Group>

      {/* `expanded`, not `in`. Mantine 9 renamed the prop, and the old name does not fail — it
          falls into ...others and lands on the DOM node, leaving expanded permanently undefined.
          The answer still fetched and still mounted (keepMounted defaults to true); it just sat
          inside a box pinned at height 0, display none, opacity 0. */}
      <Collapse expanded={open}>
        <Box mt="sm">
          {loading && <Skeleton h={120} radius="sm" />}

          {error && (
            <Alert color="warning" icon={<IconAlertTriangle size={16} />} p="xs">
              {error}
            </Alert>
          )}

          {answer && (
            <Stack gap="xs">
              <Text fz="xs" c="dimmed">
                Asked: “{answer.prompt}”
              </Text>
              <AnswerMarkdown
                text={answer.answerText}
                ownDomain={ownDomain}
                highlightTerms={[
                  ...(answer.matchedTerms ?? []),
                  ...(answer.competitorsMentioned ?? []),
                ]}
                maxHeight={460}
              />
            </Stack>
          )}
        </Box>
      </Collapse>
    </Card>
  );
}

export default function PromptSamplesDrawer({ brandId, prompt, range, onClose, ownDomain }) {
  const [samples, setSamples] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const queryId = prompt?.monitoredQueryId;

  const load = useCallback(async () => {
    if (!queryId) return;
    // Cleared here rather than in the effect, so the previous prompt's checks never render under
    // the new prompt's heading while the fetch is in flight.
    setSamples(null);
    setLoading(true);
    setError(null);
    try {
      setSamples(await fetchPromptSamples(brandId, queryId, range));
    } catch (e) {
      setError(e?.message ?? "The checks could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [brandId, queryId, range]);

  useEffect(() => {
    load();
  }, [load]);

  const meta = prompt ? presenceClassMeta(prompt.class) : null;
  const typeMeta = prompt ? queryTypeMeta(prompt.queryType) : null;

  // An unaided prompt carries a type badge reading "Unaided" and a class badge reading the same
  // thing, which is two chips saying one fact. The class badge earns its place only when it says
  // something the type does not — a Custom prompt that turned out to name the brand, say.
  const classAddsNothing =
    meta && typeMeta &&
    meta.shortLabel.toLowerCase() === typeMeta.badgeLabel.toLowerCase();

  return (
    <Drawer
      opened={Boolean(prompt)}
      onClose={onClose}
      position="right"
      size="min(920px, 96vw)"
      title={<Title order={4}>Checks for this prompt</Title>}
    >
      {prompt && (
        <Stack gap="md">
          <Card withBorder padding="sm">
            <Text fz="sm" mb="xs">
              {prompt.prompt ?? "(prompt removed)"}
            </Text>
            <Group gap="xs">
              <QueryTypeBadge queryType={prompt.queryType} />
              {meta && !classAddsNothing && (
                <Badge size="sm" color={meta.color} variant="light">
                  {meta.shortLabel}
                </Badge>
              )}
              <Text fz="xs" c="dimmed">
                {prompt.mentions} of {prompt.samples} checks mentioned your brand
                {prompt.failures > 0 && `, ${prompt.failures} excluded`}
              </Text>
            </Group>
            {meta && (
              <Text fz="xs" c="dimmed" mt={6}>
                {meta.meaning}
              </Text>
            )}
          </Card>

          {loading && <Skeleton h={220} radius="md" />}

          {error && (
            <Alert
              color="error"
              icon={<IconAlertTriangle size={18} />}
              title="Could not load the checks"
            >
              {error}
            </Alert>
          )}

          {samples?.length === 0 && (
            <Alert color="info" icon={<IconInfoCircle size={18} />}>
              No checks in this window.
            </Alert>
          )}

          {samples?.map((s) => (
            <SampleRow key={s.id} brandId={brandId} sample={s} ownDomain={ownDomain} />
          ))}
        </Stack>
      )}
    </Drawer>
  );
}
